import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, Edit, Trash2, Calendar, Eye, FileText, ChevronUp, ChevronDown, Save } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { Button, Card } from '../../components';
import { articlesService } from '../../services';

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ArticleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ArticleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: ${props => props.theme.colors.background.secondary};
  border: 1px solid ${props => props.theme.colors.border?.light || 'rgba(0,0,0,0.1)'};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const OrderControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;

  button {
    background: none;
    border: 1px solid ${props => props.theme.colors.border?.light || 'rgba(0,0,0,0.15)'};
    border-radius: 4px;
    cursor: pointer;
    padding: 2px 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.colors.text.secondary};
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
      background: ${props => props.theme.colors.primaryLight};
      color: ${props => props.theme.colors.primary};
      border-color: ${props => props.theme.colors.primary};
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }
`;

const ArticleThumb = styled.div`
  width: 56px;
  height: 40px;
  border-radius: 6px;
  background-color: ${props => props.theme.colors.background.tertiary};
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

const ArticleInfo = styled.div`
  flex: 1;
  min-width: 0;

  .title {
    font-weight: 600;
    color: ${props => props.theme.colors.text.primary};
    font-size: ${props => props.theme.typography.body};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 4px;
    color: ${props => props.theme.colors.text.tertiary};
    font-size: ${props => props.theme.typography.small};

    .meta-item {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .status {
      padding: 0.15rem 0.5rem;
      border-radius: ${props => props.theme.borderRadius.base};
      font-size: ${props => props.theme.typography.xs};
      font-weight: 500;

      &.published {
        background-color: ${props => props.theme.colors.success}20;
        color: ${props => props.theme.colors.success};
      }

      &.draft {
        background-color: ${props => props.theme.colors.warning}20;
        color: ${props => props.theme.colors.warning};
      }
    }
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
  }

  .tag {
    background: ${props => props.theme.colors.primaryLight};
    color: ${props => props.theme.colors.primary};
    padding: 1px 6px;
    border-radius: 4px;
    font-size: ${props => props.theme.typography.xs};
    font-weight: 500;
  }
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const SaveBar = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 100;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text.secondary};

  .icon {
    width: 4rem;
    height: 4rem;
    margin: 0 auto 1rem auto;
    color: ${props => props.theme.colors.text.tertiary};
  }

  h3 {
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 2rem;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: ${props => props.theme.colors.text.secondary};
`;

export const ArticlesAdmin = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const articlesData = await articlesService.getAllArticles();
      setArticles(articlesData);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const move = (index, direction) => {
    const next = [...articles];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= next.length) return;
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    setArticles(next);
    setIsDirty(true);
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      await articlesService.updateArticlesOrder(articles.map(a => a.id));
      setIsDirty(false);
    } catch (error) {
      console.error('Error saving order:', error);
      alert('Failed to save order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        await articlesService.deleteArticle(id);
        setArticles(articles.filter(a => a.id !== id));
        setIsDirty(true);
      } catch (error) {
        console.error('Error deleting article:', error);
        alert('Failed to delete article');
      }
    }
  };

  const formatDate = (article) => {
    if (article.publishDate) {
      const [year, month, day] = article.publishDate.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    }
    if (article.createdAt) {
      return article.createdAt.toDate().toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    }
    return '';
  };

  if (loading) {
    return (
      <AdminLayout title="Articles">
        <LoadingContainer>
          <p>Loading articles...</p>
        </LoadingContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Articles">
      <HeaderActions>
        <HeaderLeft>
          <h2 style={{ margin: 0, color: 'var(--color-text-primary)' }}>
            All Articles ({articles.length})
          </h2>
        </HeaderLeft>
        <Button as={Link} to="/admin/articles/new">
          <Plus size={18} />
          New Article
        </Button>
      </HeaderActions>

      {articles.length === 0 ? (
        <EmptyState>
          <FileText className="icon" />
          <h3>No articles yet</h3>
          <p>
            Start sharing your thoughts and insights by creating your first article.
            Articles help establish your expertise and connect with your audience.
          </p>
          <Button as={Link} to="/admin/articles/new" size="lg">
            <Plus size={20} />
            Write Your First Article
          </Button>
        </EmptyState>
      ) : (
        <ArticleList>
          {articles.map((article, index) => (
            <ArticleRow key={article.id}>
              <OrderControls>
                <button
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  title="Move up"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  onClick={() => move(index, 1)}
                  disabled={index === articles.length - 1}
                  title="Move down"
                >
                  <ChevronDown size={14} />
                </button>
              </OrderControls>

              <span style={{ fontSize: '13px', color: 'var(--color-text-tertiary)', width: '20px', textAlign: 'right', flexShrink: 0 }}>
                {index + 1}
              </span>

              {article.featuredImage && (
                <ArticleThumb image={article.featuredImage} />
              )}

              <ArticleInfo>
                <div className="title">{article.title}</div>
                <div className="meta">
                  {formatDate(article) && (
                    <div className="meta-item">
                      <Calendar size={12} />
                      {formatDate(article)}
                    </div>
                  )}
                  <div className={`status ${article.published ? 'published' : 'draft'}`}>
                    {article.published ? 'Published' : 'Draft'}
                  </div>
                </div>
                {article.tags && article.tags.length > 0 && (
                  <div className="tags">
                    {article.tags.slice(0, 4).map((tag, i) => (
                      <span key={i} className="tag">{tag}</span>
                    ))}
                    {article.tags.length > 4 && (
                      <span className="tag">+{article.tags.length - 4} more</span>
                    )}
                  </div>
                )}
              </ArticleInfo>

              <RowActions>
                <Button
                  as={Link}
                  to={`/admin/articles/${article.id}/edit`}
                  variant="outline"
                  size="sm"
                >
                  <Edit size={14} />
                  Edit
                </Button>
                {article.published && (
                  <Button
                    as={Link}
                    to={`/articles/${article.id}`}
                    variant="ghost"
                    size="sm"
                  >
                    <Eye size={14} />
                    View
                  </Button>
                )}
                <Button
                  onClick={() => handleDelete(article.id)}
                  variant="ghost"
                  size="sm"
                  style={{ color: 'var(--color-error)' }}
                >
                  <Trash2 size={14} />
                </Button>
              </RowActions>
            </ArticleRow>
          ))}
        </ArticleList>
      )}

      {isDirty && (
        <SaveBar>
          <Button onClick={handleSaveOrder} disabled={saving} size="lg">
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Order'}
          </Button>
        </SaveBar>
      )}
    </AdminLayout>
  );
};
